import { useEffect, useMemo, useState, useRef } from "react";
import styles from "./Home.module.scss";
import classNames from "classnames/bind";

import { FaEye } from "react-icons/fa";
import { FaGroupArrowsRotate } from "react-icons/fa6";
import { RiMoneyDollarCircleFill } from "react-icons/ri";
import { FaStar } from "react-icons/fa";
import { FaLongArrowAltUp } from "react-icons/fa";
import { FaLongArrowAltDown } from "react-icons/fa";
import ChartCard from "./components/ChartCard";
import AiInsight from "./components/AiInsight";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createAxios } from "../../../createInstance";
import { loginSuccess } from "../../../redux/authSlice";
import { getDataFacebookWithPrev, getDataTiktokWithPrev } from "../../../redux/apiRequest";

const cx = classNames.bind(styles);

const Home = () => {
    // Dropdown range
    const [open, setOpen] = useState(false);
    const [range, setRange] = useState("this_month");
    const [showCustom, setShowCustom] = useState(false);
    const [customSince, setCustomSince] = useState(""); // YYYY-MM-DD
    const [customUntil, setCustomUntil] = useState(""); // YYYY-MM-DD

    // Redux + auth
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.login.currentUser);
    const axiosJWT = useMemo(
        () => (user ? createAxios(user, dispatch, loginSuccess, navigate) : null),
        [user, dispatch, navigate]
    );

    // Data from store
    const fbState = useSelector((state) => state.facebook.facebooks?.listData);
    const tkState = useSelector((state) => state.tiktok.tiktoks?.listData);

    const slug = useMemo(() => {
        const c = user?.customer;
        return typeof c === "string" ? c : c?.slug;
    }, [user]);

    // Date helpers
    const toISO = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    };
    const toVN = (d) => {
        const day = String(d.getDate()).padStart(2, "0");
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const y = d.getFullYear();
        return `${day}/${m}/${y}`;
    };
    const getRange = (key) => {
        const now = new Date();
        let start;
        if (key === "this_week") {
            const day = now.getDay();
            const diff = (day === 0 ? 6 : day - 1); // Monday start
            start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
        } else {
            start = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        start.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (key === "custom" && customSince && customUntil) {
            const s = new Date(customSince);
            const u = new Date(customUntil);
            return {
                fb: { since: toISO(s), until: toISO(u) },
                tk: { since: toVN(s), until: toVN(u) },
            };
        } else {
            return {
                fb: { since: toISO(start), until: toISO(today) },
                tk: { since: toVN(start), until: toVN(today) },
            };
        }
    };

    const curRange = useMemo(() => getRange(range), [range, customSince, customUntil]);

    // Fetch when user/range changes
    // De-duplicate identical fetches (e.g., React StrictMode double invoke)
    const lastFetchKeyRef = useRef("");
    useEffect(() => {
        if (!slug || !axiosJWT) return;
        const key = `${slug}|fb:${curRange?.fb?.since}-${curRange?.fb?.until}|tk:${curRange?.tk?.since}-${curRange?.tk?.until}`;
        if (lastFetchKeyRef.current === key) return;
        lastFetchKeyRef.current = key;
        getDataFacebookWithPrev(slug, user?.accessToken, dispatch, axiosJWT, curRange.fb);
        getDataTiktokWithPrev(slug, user?.accessToken, dispatch, axiosJWT, curRange.tk);
        // Only re-run when slug or date range changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, curRange]);

    // Build chart datasets: Facebook base + TikTok added into impressions
    const dataSets = useMemo(() => {
        const aggregatePages = (pages) => {
            const byIso = {};
            for (const p of (pages || [])) {
                for (const r of (p.data || [])) {
                    const iso = String(r.day).slice(0, 10);
                    if (!byIso[iso]) byIso[iso] = { impressions: 0, interactions: 0, cost: 0, reviews: 0 };
                    byIso[iso].impressions += Number(r.page_impressions ?? r.page_impressions_unique ?? 0);
                    byIso[iso].interactions += Number(r.page_post_engagements ?? 0);
                    byIso[iso].reviews += Number(r.page_daily_follows ?? 0);
                }
            }
            return byIso;
        };

        const curAgg = aggregatePages(fbState?.current?.pages);
        const prevAgg = aggregatePages(fbState?.previous?.pages);
        // TikTok aggregate by ISO date if series exist
        const parseDMY = (vn) => {
            if (!vn) return null; const [d,m,y] = String(vn).split('/');
            const dd = parseInt(d,10), mm = parseInt(m,10), yy = parseInt(y,10);
            if (!dd || !mm || !yy) return null; return new Date(yy, mm-1, dd);
        };
        const yearHint = (parseDMY(curRange?.tk?.since) || new Date()).getFullYear();
        const dmToIso = (dm) => {
            const parts = String(dm || '').split('/');
            if (parts.length < 2) return null;
            const d = parseInt(parts[0],10); const m = parseInt(parts[1],10);
            if (!d || !m) return null; return `${yearHint}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        };
        const aggTk = (list) => {
            const byIso = {};
            const arr = Array.isArray(list) ? list : [];
            for (const it of arr) {
                const ser = Array.isArray(it?.series) ? it.series : [];
                for (const s of ser) {
                    const iso = dmToIso(s?.date);
                    if (!iso) continue;
                    byIso[iso] = (byIso[iso] || 0) + Number(s?.totalViews || 0);
                }
            }
            return byIso;
        };
        const tkCurByIso = aggTk(tkState?.current);
        const tkPrevByIso = aggTk(tkState?.previous);
        // TikTok interactions (likesCount) aggregation by date
        const aggTkLikes = (list) => {
            const byIso = {};
            const arr = Array.isArray(list) ? list : [];
            for (const it of arr) {
                const ser = Array.isArray(it?.series) ? it.series : [];
                for (const s of ser) {
                    const iso = dmToIso(s?.date);
                    if (!iso) continue;
                    byIso[iso] = (byIso[iso] || 0) + Number(s?.likesCount || 0);
                }
            }
            return byIso;
        };
        const tkCurLikeByIso = aggTkLikes(tkState?.current);
        const tkPrevLikeByIso = aggTkLikes(tkState?.previous);
        const curDates = Object.keys(curAgg)
            .filter(iso => !curRange?.fb?.since || iso >= curRange.fb.since)
            .sort();

        const buildSeries = (key) => curDates.map((iso) => {
            const d = new Date(iso);
            const label = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
            const pm = new Date(d);
            pm.setMonth(pm.getMonth() - 1);
            const pmIso = `${pm.getFullYear()}-${String(pm.getMonth() + 1).padStart(2, "0")}-${String(pm.getDate()).padStart(2, "0")}`;
            const compareVal = prevAgg[pmIso]?.[key] || 0;
            if (key === 'impressions') {
                const val = (curAgg[iso][key] || 0) + (tkCurByIso[iso] || 0);
                const cmp = (prevAgg[pmIso]?.[key] || 0) + (tkPrevByIso[pmIso] || 0);
                return { date: label, value: val, compare: cmp };
            } else if (key === 'interactions') {
                const val = (curAgg[iso][key] || 0) + (tkCurLikeByIso[iso] || 0);
                const cmp = (prevAgg[pmIso]?.[key] || 0) + (tkPrevLikeByIso[pmIso] || 0);
                return { date: label, value: val, compare: cmp };
            }
            return { date: label, value: curAgg[iso][key] || 0, compare: compareVal };
        });

        return {
            impressions: buildSeries("impressions"),
            interactions: buildSeries("interactions"),
            cost: buildSeries("cost"),
            reviews: buildSeries("reviews"),
        };
    }, [fbState, tkState, curRange]);

    // Header summaries
    const summarize = (arr) => {
        const total = (arr || []).reduce((s, x) => s + (Number(x.value) || 0), 0);
        const n = (arr || []).length;
        const last = n > 0 ? arr[n - 1].value : 0;
        const lastCompare = n > 0 ? arr[n - 1].compare || 0 : 0;
        const deltaPct = lastCompare > 0 ? Math.round(((last - lastCompare) / lastCompare) * 100) : 0;
        const trendUp = deltaPct >= 0;
        return { total, deltaPct, trendUp };
    };
    const sImpr = summarize(dataSets.impressions);
    const sInter = summarize(dataSets.interactions);
    const sCost = summarize(dataSets.cost);
    const sReviews = summarize(dataSets.reviews);

    const options = [
        // { value: "today", label: "Hôm nay" },
        { value: "this_week", label: "Tuần này" },
        { value: "this_month", label: "Tháng này" },
        { value: "custom", label: "Tùy chọn" },
    ];

    const selectedLabelRaw = options.find((o) => o.value === range)?.label;
    const selectedLabel = (range === "custom" && customSince && customUntil)
        ? `${customSince} → ${customUntil}`
        : selectedLabelRaw;

    const handleSelect = (value) => {
        setRange(value);
        if (value === "custom") {
            setShowCustom(true);
        } else {
            setShowCustom(false);
        }
        setOpen(false);
    };

    const applyCustom = () => {
        if (!customSince || !customUntil) return;
        if (new Date(customSince) > new Date(customUntil)) return;
        setRange("custom");
        setShowCustom(false);
    };
    const cancelCustom = () => {
        setShowCustom(false);
        if (!customSince && !customUntil) setRange("this_month");
    };

    // Table per-channel aggregates
    const fbTotals = useMemo(() => {
        const arr = dataSets;
        const sumVal = (series) => series.reduce((s, x) => s + (x.value || 0), 0);
        const sumCmp = (series) => series.reduce((s, x) => s + (x.compare || 0), 0);
        const totalImpr = sumVal(arr.impressions);
        const totalImprPrev = sumCmp(arr.impressions);
        const totalInter = sumVal(arr.interactions);
        const totalInterPrev = sumCmp(arr.interactions);
        const totalCost = sumVal(arr.cost);
        const totalCostPrev = sumCmp(arr.cost);
        const totalReviews = sumVal(arr.reviews);
        const totalReviewsPrev = sumCmp(arr.reviews);
        const pct = (cur, prev) => prev > 0 ? Math.round(((cur - prev) / prev) * 100) : 0;
        return {
            impressions: totalImpr,
            interactions: totalInter,
            cost: totalCost,
            reviews: totalReviews,
            chgImpr: pct(totalImpr, totalImprPrev),
            chgInter: pct(totalInter, totalInterPrev),
            chgCost: pct(totalCost, totalCostPrev),
            chgReviews: pct(totalReviews, totalReviewsPrev),
        };
    }, [dataSets]);

    const tkTotals = useMemo(() => {
        const curList = Array.isArray(tkState?.current) ? tkState.current : tkState?.current || [];
        const prevList = Array.isArray(tkState?.previous) ? tkState.previous : tkState?.previous || [];
        const sum = (list, key) => (Array.isArray(list) ? list : []).reduce((s, it) => s + Number(it?.diff?.[key] || 0), 0);
        const impressions = sum(curList, 'totalViews');
        const interactions = sum(curList, 'likesCount');
        const impressionsPrev = sum(prevList, 'totalViews');
        const interactionsPrev = sum(prevList, 'likesCount');
        const pct = (cur, prev) => prev > 0 ? Math.round(((cur - prev) / prev) * 100) : 0;
        return { impressions, interactions, cost: 0, reviews: 0, chgImpr: pct(impressions, impressionsPrev), chgInter: pct(interactions, interactionsPrev) };
    }, [tkState]);

    return (
        <main className={cx("main")}>
            <div className={cx("section", "sectionHeader")}>
                <h3 className={cx("title")}>Tổng quan</h3>
                <div className={cx("dropdown")}>
                    <button
                        type="button"
                        className={cx("dropdownToggle")}
                        onClick={() => setOpen(!open)}
                    >
                        {selectedLabel}
                        <span className={cx("arrow")}>▼</span>
                    </button>
                    {open && (
                        <ul className={cx("dropdownMenu")}>
                            {options.map((opt) => (
                                <li key={opt.value} onClick={() => handleSelect(opt.value)}>
                                    {opt.label}
                                </li>
                            ))}
                        </ul>
                    )}
                    {showCustom && (
                        <div className={cx("customPanel")}
                        >
                            <span className={cx("label")}>Từ</span>
                            <input type="date" value={customSince} onChange={(e) => setCustomSince(e.target.value)} />
                            <span className={cx("sep")}>→</span>
                            <span className={cx("label")}>đến</span>
                            <input type="date" value={customUntil} onChange={(e) => setCustomUntil(e.target.value)} />
                            <div className={cx("actions")}>
                                <button className={cx("btn")} onClick={cancelCustom}>Hủy</button>
                                <button className={cx("btn", "primary")} onClick={applyCustom}>Áp dụng</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={cx("section", "statsRow")}>
                <div className={cx('block')}>
                    <FaEye />
                    <div className={cx('description')}>
                        <h4>{sImpr.total.toLocaleString('vi-VN')}</h4>
                        <h5>Lượt hiển thị</h5>
                        <div className={cx('change', sImpr.trendUp ? 'up' : 'down')}>
                            {Math.abs(sImpr.deltaPct)}%
                            {sImpr.trendUp ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                        </div>
                    </div>
                </div>
                <div className={cx('block')}>
                    <FaGroupArrowsRotate />
                    <div className={cx('description')}>
                        <h4>{sInter.total.toLocaleString('vi-VN')}</h4>
                        <h5>Lượt tương tác</h5>
                        <div className={cx('change', sInter.trendUp ? 'up' : 'down')}>
                            {Math.abs(sInter.deltaPct)}%
                            {sInter.trendUp ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                        </div>
                    </div>
                </div>
                <div className={cx('block')}>
                    <RiMoneyDollarCircleFill />
                    <div className={cx('description')}>
                        <h4>{sCost.total.toLocaleString('vi-VN')}</h4>
                        <h5>Chi phí quảng cáo</h5>
                        <div className={cx('change', sCost.trendUp ? 'up' : 'down')}>
                            {Math.abs(sCost.deltaPct)}%
                            {sCost.trendUp ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                        </div>
                    </div>
                </div>
                <div className={cx('block')}>
                    <FaStar className={cx("star")} />
                    <div className={cx('description')}>
                        {/* <h4>{sReviews.total.toLocaleString('vi-VN')}</h4> */}
                        <h4>0</h4>
                        <h5>Đánh giá</h5>
                        {/* <div className={cx('change', sReviews.trendUp ? 'up' : 'down')}>
                            {Math.abs(sReviews.deltaPct)}%
                            {sReviews.trendUp ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                        </div> */}
                        <div className={cx('change', 'up')}>
                            0%
                            <FaLongArrowAltUp /> 
                        </div>
                    </div>
                </div>
            </div>

            <div className={cx("section", "twoColSection")}>
                <div className={cx("wrap-panel")}>
                    <div className={cx("panel")}>
                        <ChartCard dataSets={dataSets} />
                    </div>
                    <div className={cx("panel")}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Kênh quản lý</th>
                                    <th>Lượt hiển thị</th>
                                    <th>Lượt tương tác</th>
                                    <th>Chi phí quảng cáo</th>
                                    <th>Đánh giá</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={cx("title-td")}>Facebook </td>
                                    <td className={cx("title-td")}>
                                        <div>{fbTotals.impressions.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', fbTotals.chgImpr >= 0 ? 'up' : 'down')}>
                                            {Math.abs(fbTotals.chgImpr)}%{fbTotals.chgImpr >= 0 ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                                        </div>
                                    </td>

                                    <td className={cx("title-td")}>
                                        <div>{fbTotals.interactions.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', fbTotals.chgInter >= 0 ? 'up' : 'down')}>
                                            {Math.abs(fbTotals.chgInter)}%{fbTotals.chgInter >= 0 ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                                        </div>
                                    </td>
                                    <td className={cx("title-td")}>
                                        <div>{fbTotals.cost.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', fbTotals.chgCost >= 0 ? 'up' : 'down')}>
                                            {Math.abs(fbTotals.chgCost)}%{fbTotals.chgCost >= 0 ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                                        </div>
                                    </td>
                                    <td className={cx("title-td")}>
                                        <div>{fbTotals.reviews.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', fbTotals.chgReviews >= 0 ? 'up' : 'down')}>
                                            {Math.abs(fbTotals.chgReviews)}%{fbTotals.chgReviews >= 0 ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className={cx("title-td")}>Tiktok </td>
                                    <td className={cx("title-td")}>
                                        <div>{tkTotals.impressions.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', tkTotals.chgImpr >= 0 ? 'up' : 'down')}>
                                            {Math.abs(tkTotals.chgImpr)}%{tkTotals.chgImpr >= 0 ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                                        </div>
                                    </td>

                                    <td className={cx("title-td")}>
                                        <div>{tkTotals.interactions.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', tkTotals.chgInter >= 0 ? 'up' : 'down')}>
                                            {Math.abs(tkTotals.chgInter)}%{tkTotals.chgInter >= 0 ? <FaLongArrowAltUp /> : <FaLongArrowAltDown />}
                                        </div>
                                    </td>
                                    <td className={cx("title-td")}>
                                        <div>{tkTotals.cost.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', 'up')}>0%<FaLongArrowAltUp /></div>
                                    </td>
                                    <td className={cx("title-td")}>
                                        <div>{tkTotals.reviews.toLocaleString('vi-VN')}</div>
                                        <div className={cx('change', 'up')}>0%<FaLongArrowAltUp /></div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <AiInsight />
            </div>
        </main>
    );
};

export default Home;
